import './clarity.ts'
import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import { AuthenticationGuard } from './components/auth/AuthenticationGuard'
import { Home } from './views/employee/Home'
import { ComponentLibrary } from './views/componentLibrary/Index'
import { ComponentLibraryBaseLayout } from './components/layouts/ComponentLibraryBaseLayout'
import { Typography } from './views/componentLibrary/Typography'
import { BaseLayout } from './components/layouts/BaseLayout'
import { AdminViewGuard } from './components/auth/AuthorizationGuard'
import { Colors } from './views/componentLibrary/Colors'
import { Grids } from './views/componentLibrary/Grid'
import { Buttons } from './views/componentLibrary/Buttons'
import { Icons } from './views/componentLibrary/Icons.tsx'
import { ServiceIndex } from './views/admin/services/Index.tsx'
import { Employees } from './views/admin/Employees.tsx'
import { Invoices } from './views/admin/Invoices.tsx'
import { Archive } from './views/employee/knowledgeBank/Index.tsx'
import { PolicyAdmin } from './views/admin/services/Policy.tsx'
import { TrainingIndex } from './views/employee/trainings/Index.tsx'
import { Training } from './views/employee/trainings/Training.tsx'
import { ContentfulBlocks } from './views/componentLibrary/ContentfulBlocks.tsx'
import { Article } from './views/employee/knowledgeBank/Article.tsx'
import { Podcast } from './views/employee/knowledgeBank/Podcast.tsx'
import { Webinar } from './views/employee/knowledgeBank/Webinar.tsx'
import { EmployeeSupportIndex } from './views/employee/support/Index.tsx'
import { HomeAdmin } from './views/admin/Home.tsx'
import { Service } from './views/admin/services/Service.tsx'
import { Certification } from './views/admin/certification/Index.tsx'
import { CertificationQuestions } from './views/admin/certification/Questions.tsx'
import { CertificationUpload } from './views/admin/certification/Upload.tsx'
import { CertificationSubmit } from './views/admin/certification/Submit.tsx'
import { ErrorPage } from './views/Error.tsx'
import { Login } from './views/Login.tsx'
import { SsoLogin } from './views/SsoLogin.tsx'
import { LoggedOut } from './views/LoggedOut.tsx'
import { Profile } from './views/Profile.tsx'
import { NotFound } from './views/Notfound.tsx'
import { Screening } from './views/employee/healthServices/screening/Screening.tsx'
import { HealthCall } from './views/employee/healthServices/HealthCall/HealthCall.tsx'
import { Statistics } from './views/admin/Statistics.tsx'
import { EmployeeSupport } from './views/employee/support/Support.tsx'
import { AdminSupportIndex } from './views/admin/support/Index.tsx'
import { AdminSupport } from './views/admin/support/Support.tsx'
import { CreateAccount } from './views/CreateAccount.tsx'
import { DataPolicyPage } from './views/policy/DataPolicyPage.tsx'
import { PolicyDeclinedPage } from './views/policy/PolicyDeclinedPage.tsx'
import { YourHealthIndex } from './views/employee/yourHealth/Index.tsx'
import { PortalLayout } from './components/layouts/PortalLayout.tsx'
import { Questions } from './views/employee/healthServices/screening/Questions.tsx'
import { Inputs } from './views/componentLibrary/Inputs.tsx'
import { Miscellaneous } from './views/componentLibrary/miscellaneous.tsx'
import { SubmitScreening } from './views/employee/healthServices/screening/submitScreen/index.tsx'
import { useRouteAnalytics } from './hooks/analytics/useRouteAnalytics.ts'
import { LegacyRedirect } from './components/LegacyRedirect'
import { TestEmailTemplates } from './views/componentLibrary/TestEmailTemplate.tsx'
import { ScreeningBookHealthCall } from './views/employee/healthServices/screening/BookHealthCall.tsx'
import { HealthPlanCardsComponentLibrary } from './views/componentLibrary/HealthPlanCards.tsx'
import { OrganisationNeedsComponentLibrary } from './views/componentLibrary/OrganisationNeeds.tsx'
import { HealthCallCalendar } from './views/employee/healthServices/HealthCall/HealthCallCalendar.tsx'
import { Messages } from './views/employee/messages/Index.tsx'
import { NewConversation } from './views/employee/messages/NewConversation.tsx'
import { Conversation } from './views/employee/messages/Conversation.tsx'
import { FeatureFlagGuard } from './components/auth/FeatureFlagGuard.tsx'
import { FeatureFlagId } from './api/ester-b2b-api/private/featureFlags/types.ts'
import { MarketGuard } from './components/auth/MarketGuard.tsx'
import { SupportedMarket } from '@estercare/ester-shared'

export const App = () => {
  useRouteAnalytics()
  return (
    <BaseLayout>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/login/sso/:connection" element={<SsoLogin />} />
        <Route path="/create-account" element={<CreateAccount />} />
        <Route path="/logged-out" element={<LoggedOut />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/data-policy" element={<DataPolicyPage />} />
        <Route path="/policy-declined" element={<PolicyDeclinedPage />} />
        <Route element={<AuthenticationGuard />}>
          <Route path="/" element={<Navigate to="/portal" replace />} />
          <Route path="*" element={<NotFound />} />
          <Route path="profile" element={<Profile />} />
          <Route path="admin" element={<AdminViewGuard />}>
            <Route index element={<HomeAdmin />} />
            <Route path="dashboard" element={<HomeAdmin />} />
            <Route path="services" element={<Outlet />}>
              <Route index element={<ServiceIndex />} />
              <Route path="policy" element={<PolicyAdmin />} />
              <Route path=":id" element={<Service />} />
            </Route>
            <Route path="certification" element={<Outlet />}>
              <Route index element={<Certification />} />
              <Route path="questions" element={<CertificationQuestions />} />
              <Route path="upload" element={<CertificationUpload />} />
              <Route path="submit" element={<CertificationSubmit />} />
            </Route>
            <Route path="statistics" element={<Statistics />} />
            <Route path="employees" element={<Employees />} />
            <Route path="billing" element={<Invoices />} />
            <Route path="support" element={<Outlet />}>
              <Route index element={<AdminSupportIndex />} />
              <Route path=":id" element={<AdminSupport />} />
            </Route>
          </Route>
          <Route path="portal" element={<PortalLayout />}>
            <Route index element={<Home />} />
            <Route path="home" element={<Home />} />
            <Route path="health-services" element={<Outlet />}>
              <Route index element={<Navigate to="/portal" replace />} />
              <Route path="screening" element={<Outlet />}>
                <Route index element={<Screening />} />
                <Route path="questions" element={<Questions />} />
                <Route path="submit" element={<SubmitScreening />} />
                <Route path="book-health-call" element={<ScreeningBookHealthCall />} />
              </Route>

              {/* Health call routes restricted to Swedish market */}
              <Route element={<MarketGuard allowedMarkets={[SupportedMarket.SWEDEN]} showUnavailableMessage={true} />}>
                <Route path=":id" element={<HealthCall />} />
                <Route path=":id/book" element={<HealthCallCalendar />} />
              </Route>
            </Route>
            <Route path="training" element={<Outlet />}>
              <Route index element={<TrainingIndex />} />
              <Route path=":id" element={<Training />} />
            </Route>
            <Route path="your-health/:id" element={<Outlet />}>
              <Route index element={<YourHealthIndex />} />
            </Route>
            <Route path="resource-center" element={<Outlet />}>
              <Route index element={<Archive />} />
              <Route path="articles/:id" element={<Article />} />
              <Route path="podcasts/:id" element={<Podcast />} />
              <Route path="webinars/:id" element={<Webinar />} />
            </Route>
            <Route path="support" element={<Outlet />}>
              <Route index element={<EmployeeSupportIndex />} />
              <Route path=":id" element={<EmployeeSupport />} />
            </Route>
            <Route path="messages" element={<FeatureFlagGuard flagId={FeatureFlagId.USE_PORTAL_CHAT} />}>
              <Route index element={<Messages />} />
              <Route path="new" element={<NewConversation />} />
              <Route path=":caseId" element={<Conversation />} />
            </Route>

            {/* Legacy redirects for old v2 tab slugs */}
            <Route
              path="your-health/health-plan-v2"
              element={<Navigate to="/portal/your-health/health-plan" replace />}
            />
            <Route
              path="your-health/wellbeing-v2"
              element={<Navigate to="/portal/your-health/health-progress" replace />}
            />
            <Route
              path="your-health/wellbeing"
              element={<Navigate to="/portal/your-health/health-progress" replace />}
            />

            {/* Legacy redirects for backwards compatibility with AI-generated links */}
            <Route
              path="halsotjanster/halsosamtal"
              element={<Navigate to="/portal/health-services/health-consultation" replace />}
            />
            <Route path="din-halsa/halsoplan" element={<Navigate to="/portal/your-health/health-plan" replace />} />
            <Route path="kunskapsbank" element={<Navigate to="/portal/resource-center" replace />} />
            <Route
              path="kunskapsbank/artiklar/:id"
              element={<LegacyRedirect to="/portal/resource-center/articles/:id" />}
            />
            <Route
              path="kunskapsbank/podcasts/:id"
              element={<LegacyRedirect to="/portal/resource-center/podcasts/:id" />}
            />
          </Route>
          <Route path="component-library" element={<ComponentLibraryBaseLayout />}>
            <Route index element={<ComponentLibrary />} />
            <Route path="colors" element={<Colors />} />
            <Route path="typography" element={<Typography />} />
            <Route path="grid" element={<Grids />} />
            <Route path="buttons" element={<Buttons />} />
            <Route path="inputs" element={<Inputs />} />
            <Route path="icons" element={<Icons />} />
            <Route path="contentful-blocks" element={<ContentfulBlocks />} />
            <Route path="test-email-templates" element={<TestEmailTemplates />} />
            <Route path="miscellaneous" element={<Miscellaneous />} />
            <Route path="healthplan-cards" element={<HealthPlanCardsComponentLibrary />} />
            <Route path="organisation-needs" element={<OrganisationNeedsComponentLibrary />} />
          </Route>
        </Route>
      </Routes>
    </BaseLayout>
  )
}
